import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Table from 'components/customTable';
import { FindJobs } from 'components/findJobs';
import { getUserOrGeoLocations, removeSpecialCharacter } from 'utils';
import { CMP_SUBPAGES } from 'utils/constant';

const answersPropTypes = PropTypes.shape({
  answerDisplay: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  dapUrl: PropTypes.string,
  isTable: PropTypes.bool,
  question: PropTypes.string.isRequired,
  tableConfig: PropTypes.object,
});

const propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.shape({
    answers: PropTypes.arrayOf(answersPropTypes),
    entityType: PropTypes.string,
  }),
  elementId: PropTypes.string,
  hideFindJobsCallout: PropTypes.bool,
  src: PropTypes.string,
  criteria: PropTypes.object,
  padding: PropTypes.bool,
  findJobsTagName: PropTypes.string,
  children: PropTypes.node,
  redirectPage: PropTypes.string,
  redirectUrl: PropTypes.string,
  showBottomLink: PropTypes.bool,
  showTopSeparator: PropTypes.bool,
};

const QuestionAndAnswerSection = ({
  title,
  data,
  criteria = {},
  elementId = '',
  hideFindJobsCallout = false,
  findJobsTagName = 'h3',
  children = null,
  src = '',
  redirectPage = '',
  redirectUrl = '',
  showBottomLink = true,
  showTopSeparator = true,
}) => {
  const user = useSelector(({ user }) => user);

  const ansCategoryPageLink = useMemo(() => {
    let url = '/answers/';
    let title = '';

    switch (data?.entityType) {
      case 'JOB_TITLE':
        if (criteria?.industry) {
          url += `${removeSpecialCharacter(criteria.industry)}-questions/`;
          title = criteria?.industry;
        }
        break;
      case 'CITY':
      case 'STATE':
        url += 'location-questions/';
        title = 'location';
        break;
      case 'COMPANY':
        url += 'company-questions/';
        title = 'company';
        break;
      case 'JOB_SEARCH':
        url += 'job-search-questions/';
        title = 'job search';
        break;
      default:
        break;
    }

    return { url: url.toLowerCase(), title };
  }, [data?.entityType, criteria?.industry]);

  if (!data?.answers?.length) {
    return <></>;
  }

  const getFindJobsProps = () => {
    if (hideFindJobsCallout) return {};

    const query = {
      redirectPage: redirectPage || CMP_SUBPAGES.JOBS,
      jobTitle: criteria?.title ?? '',
      redirectUrl: redirectUrl || '',
    };

    const locations = getUserOrGeoLocations(user?.userLocation ?? [], criteria ?? {});
    if (locations?.length) {
      const { displayName } = locations[0];
      query.userLocation = displayName;
    }

    return query;
  };

  const renderContent = (item) => {
    const { isTable, tableConfig, answerDisplay } = item;

    if (isTable && tableConfig) {
      return (
        <div className="qna-answer newContentBlurbContainer z-p-20">
          <Table
            columnConfig={tableConfig}
            bodyData={answerDisplay}
            defaultRowsDisplay={10}
            displayRowsPerExpand={10}
            scrollOnMobile
          />
        </div>
      );
    }

    return (
      <div
        className="qna-answer newContentBlurbContainer z-p-20"
        dangerouslySetInnerHTML={{ __html: answerDisplay }}
      />
    );
  };

  return (
    <>
      {showTopSeparator && <div className="section-separator-large" />}

      <section id={elementId} className="qna-section d-flex flex-column flex-lg-row justify-content-between">
        <div className={classNames('qna-container d-block w-100 w-730')}>
          <h2 className="qna-title brandonH2 z-mb-48">{title}</h2>
          {data.answers.map((item) => (
            <div className="qna-item border rounded z-mt-36" key={item.question}>
              <h3 className="qna-question text-capitalize border-bottom z-p-20 mb-0">
                {item.dapUrl ? (
                  <a className="blackColor" href={item.dapUrl}>
                    {item.question}
                  </a>
                ) : (
                  item.question
                )}
              </h3>
              {renderContent(item)}
            </div>
          ))}
          {children}
          {!!ansCategoryPageLink.url && showBottomLink && (
            <div className="z-mt-36">
              Have more questions?{' '}
              <a href={ansCategoryPageLink.url} className="alink font-weight-600">
                {' '}
                See all answers to common {(ansCategoryPageLink?.title || '').toLowerCase()}{' '}
                questions.
              </a>
            </div>
          )}
        </div>
        {!hideFindJobsCallout && (
          <div className="z-ml-lg-30">
            <FindJobs
              src={src}
              TitleTagName={findJobsTagName}
              className="sticky-top find-jobs-callout mt-5 mt-lg-0"
              {...getFindJobsProps()}
            />
          </div>
        )}
      </section>
    </>
  );
};

QuestionAndAnswerSection.propTypes = propTypes;

export default QuestionAndAnswerSection;
